import { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Landmark, Plus, Trash2, Save, Download, CalendarClock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CalendarioBancarioPage() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nuevo, setNuevo] = useState({ fecha: '', nombre: '' });
  const [anioImport, setAnioImport] = useState(new Date().getFullYear());
  const [prueba, setPrueba] = useState({ fecha: new Date().toISOString().slice(0, 10), tarde: true, res: null });

  const load = useCallback(async () => {
    try { setCfg((await api.get('/config/calendario-bancario')).data); }
    catch { toast.error('No se pudo cargar el calendario bancario'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const guardar = async (parcial) => {
    setSaving(true);
    try {
      const r = await api.put('/config/calendario-bancario', parcial);
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success('Calendario bancario guardado');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error guardando'); }
    finally { setSaving(false); }
  };

  const agregarFeriado = () => {
    if (!nuevo.fecha) { toast.error('Indica la fecha'); return; }
    if (cfg.feriados.some((f) => f.fecha === nuevo.fecha)) { toast.error('Esa fecha ya está'); return; }
    const feriados = [...cfg.feriados, { fecha: nuevo.fecha, nombre: nuevo.nombre || 'Feriado bancario' }].sort((a, b) => a.fecha.localeCompare(b.fecha));
    setNuevo({ fecha: '', nombre: '' });
    guardar({ feriados });
  };

  const importarLegales = async () => {
    try {
      const r = await api.post(`/config/calendario-bancario/importar-feriados-legales/${anioImport}`);
      toast.success(`${r.data.importados} feriados legales ${anioImport} agregados al calendario bancario`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error importando'); }
  };

  const probar = async () => {
    try {
      const r = await api.get('/config/calendario-bancario/siguiente-habil', { params: { fecha: prueba.fecha, tarde: prueba.tarde } });
      setPrueba((p) => ({ ...p, res: r.data }));
    } catch { toast.error('Fecha inválida'); }
  };

  if (!cfg) return <div className="p-6 text-sm text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-1" /> Cargando…</div>;

  const setBanco = (i, campo, valor) => setCfg((c) => ({ ...c, bancos: c.bancos.map((b, j) => (j === i ? { ...b, [campo]: valor } : b)) }));

  return (
    <div className="space-y-5 page-enter" data-testid="calendario-bancario-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Landmark className="h-6 w-6 text-sky-700" /> Calendario Bancario</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Independiente del calendario laboral. Sábados, domingos y el <b>31 de diciembre</b> son siempre inhábiles bancarios; aquí agregas el resto.
          Define la fecha esperada en cartola de cada egreso: registrado desde el ERP antes de la hora de corte → mismo día hábil; después → siguiente día hábil bancario.
          Los egresos que llegan desde Alegra (sin hora de registro) y los pagos con fecha distinta a hoy se consideran <b>antes del corte</b> → mismo día hábil del pago.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Feriados bancarios</CardTitle>
            <CardDescription>Fechas configuradas ({cfg.feriados.length}) + 31-dic fijo cada año.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2 items-end flex-wrap">
              <div><Label className="text-xs">Fecha</Label><Input type="date" value={nuevo.fecha} onChange={(e) => setNuevo({ ...nuevo, fecha: e.target.value })} className="h-8 text-xs w-[150px]" data-testid="input-feriado-fecha" /></div>
              <div className="flex-1 min-w-[160px]"><Label className="text-xs">Nombre</Label><Input value={nuevo.nombre} onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} placeholder="Feriado bancario" className="h-8 text-xs" data-testid="input-feriado-nombre" /></div>
              <Button size="sm" onClick={agregarFeriado} disabled={saving} data-testid="btn-agregar-feriado"><Plus className="h-3.5 w-3.5 mr-1" /> Agregar</Button>
            </div>
            <div className="flex gap-2 items-end">
              <div><Label className="text-xs">Importar feriados legales del año</Label><Input type="number" value={anioImport} onChange={(e) => setAnioImport(parseInt(e.target.value, 10) || anioImport)} className="h-8 text-xs w-[100px]" data-testid="input-anio-import" /></div>
              <Button size="sm" variant="outline" onClick={importarLegales} data-testid="btn-importar-legales"><Download className="h-3.5 w-3.5 mr-1" /> Importar (copia)</Button>
            </div>
            <div className="max-h-[360px] overflow-y-auto rounded border divide-y" data-testid="lista-feriados-bancarios">
              {cfg.feriados_fijos?.map((f) => (
                <div key={f.fecha} className="flex items-center justify-between px-3 py-1.5 text-xs bg-slate-50 text-slate-500">
                  <span className="font-mono">{f.fecha}</span><span className="flex-1 ml-3">{f.nombre}</span><span className="text-[10px]">fijo</span>
                </div>
              ))}
              {cfg.feriados.map((f) => (
                <div key={f.fecha} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`feriado-${f.fecha}`}>
                  <span className="font-mono">{f.fecha}</span><span className="flex-1 ml-3">{f.nombre}</span>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => guardar({ feriados: cfg.feriados.filter((x) => x.fecha !== f.fecha) })} data-testid={`btn-quitar-feriado-${f.fecha}`}>
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              ))}
              {cfg.feriados.length === 0 && <p className="px-3 py-4 text-xs text-muted-foreground text-center">Sin feriados configurados. Importa los feriados legales del año para partir.</p>}
            </div>
          </CardContent>
        </Card>

        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Reglas de conciliación</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Hora de corte (Chile)</Label><Input type="time" value={cfg.hora_corte} onChange={(e) => setCfg({ ...cfg, hora_corte: e.target.value })} className="h-8 text-xs" data-testid="input-hora-corte" /></div>
                <div><Label className="text-xs">Largo nombre en cartola (default)</Label><Input type="number" min={5} max={60} value={cfg.longitud_nombre} onChange={(e) => setCfg({ ...cfg, longitud_nombre: parseInt(e.target.value, 10) || 23 })} className="h-8 text-xs" data-testid="input-longitud-nombre" /></div>
              </div>
              <div>
                <Label className="text-xs">Bancos conciliables (cuenta Alegra ↔ cuenta de la cartola)</Label>
                <div className="space-y-2 mt-1">
                  {cfg.bancos.map((b, i) => (
                    <div key={i} className="grid grid-cols-[80px_1fr_130px_60px_28px] gap-2 items-center" data-testid={`banco-row-${i}`}>
                      <Input type="number" value={b.alegra_bank_id} onChange={(e) => setBanco(i, 'alegra_bank_id', parseInt(e.target.value, 10) || 0)} className="h-8 text-xs" title="ID banco Alegra" />
                      <Input value={b.nombre} onChange={(e) => setBanco(i, 'nombre', e.target.value)} className="h-8 text-xs" placeholder="Nombre" />
                      <Input value={b.cuenta_cartola} onChange={(e) => setBanco(i, 'cuenta_cartola', e.target.value)} className="h-8 text-xs font-mono" placeholder="N° cuenta cartola" />
                      <Input type="number" value={b.longitud_nombre} onChange={(e) => setBanco(i, 'longitud_nombre', parseInt(e.target.value, 10) || 23)} className="h-8 text-xs" title="Largo nombre" />
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setCfg({ ...cfg, bancos: cfg.bancos.filter((_, j) => j !== i) })}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => setCfg({ ...cfg, bancos: [...cfg.bancos, { alegra_bank_id: 0, nombre: '', cuenta_cartola: '', longitud_nombre: cfg.longitud_nombre }] })} data-testid="btn-agregar-banco"><Plus className="h-3.5 w-3.5 mr-1" /> Banco</Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Los egresos pagados desde cuentas que no están aquí (tarjetas, fondos por rendir) quedan como "Sin cartola".</p>
              </div>
              <Button onClick={() => guardar({ hora_corte: cfg.hora_corte, longitud_nombre: cfg.longitud_nombre, bancos: cfg.bancos })} disabled={saving} data-testid="btn-guardar-reglas">
                <Save className="h-4 w-4 mr-1" /> {saving ? 'Guardando…' : 'Guardar reglas'}
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Probar regla</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <div className="flex gap-2 items-end flex-wrap">
                <div><Label className="text-xs">Fecha de pago</Label><Input type="date" value={prueba.fecha} onChange={(e) => setPrueba({ ...prueba, fecha: e.target.value })} className="h-8 text-xs w-[150px]" data-testid="input-prueba-fecha" /></div>
                <label className="flex items-center gap-1 text-xs pb-2"><input type="checkbox" checked={prueba.tarde} onChange={(e) => setPrueba({ ...prueba, tarde: e.target.checked })} data-testid="check-prueba-tarde" /> después de las {cfg.hora_corte}</label>
                <Button size="sm" variant="outline" onClick={probar} data-testid="btn-probar">Calcular</Button>
              </div>
              {prueba.res && (
                <p className="text-sm" data-testid="resultado-prueba">
                  Fecha esperada en cartola: <b>{prueba.res.fecha_cartola_esperada}</b> · Código: <b className="font-mono">{prueba.res.codigo}</b>
                  {!prueba.res.es_habil_bancario && <span className="text-amber-700"> · la fecha de pago es inhábil bancario</span>}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
